// ProposalDetail.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Business,
  Email,
  LocationOn,
  Person,
  OpenInNew,
  Handshake,
  Engineering,
  AttachFile,
  Description,
} from "@mui/icons-material";
import { useDocument } from "../../hooks/useDocument";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import {
  PROPOSAL_STATUS,
  PROPOSAL_STATUS_LABELS,
} from "../../utils/models/proposalModel";
import { CONTRACT_STATUS } from "../../utils/models/contractModel";
import {
  ENTRY_CHANNELS,
  ENTRY_CHANNEL_LABELS,
} from "../../utils/models/clientModel";

const ProposalDetail = ({ proposalId, onEdit }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { add: addDoc } = useAuditFirestore("contracts");
  const { document: proposal, loading } = useDocument("proposals", proposalId);
  const { data: contacts } = useCollection("contacts");
  const { update: updateProposal } = useAuditFirestore("proposals");
  const { data: clients } = useCollection("clients");
  const [updating, setUpdating] = useState(false);

  const formatAddress = (addr) => {
    if (!addr || !addr.street) return "—";
    return `${addr.street}, ${addr.city}, ${addr.province || addr.state || ""}, ${addr.zipCode}, ${addr.country}`;
  };

  // Obtener el contacto del canal de entrada si existe
  const channelContact =
    proposal?.entryChannelContactId && contacts
      ? contacts.find((c) => c.id === proposal.entryChannelContactId)
      : null;

  // Determinar si mostrar información del contacto del canal
  const showChannelContact =
    (proposal?.entryChannel === ENTRY_CHANNELS.COMMERCIAL ||
      proposal?.entryChannel === ENTRY_CHANNELS.EPC) &&
    channelContact;

  const handleAccept = async () => {
    setUpdating(true);
    try {
      await updateProposal(proposalId, {
        status: PROPOSAL_STATUS.ACCEPTED,
        updatedAt: new Date().toISOString(),
      });

      const client = clients?.find((c) => c.id === proposal.clientId);

      const contractData = {
        proposalId: proposal.id,
        proposalTitle: proposal.title,
        title: proposal.title,
        clientId: proposal.clientId,
        clientName: proposal.clientName,
        clientType: client?.type || "individual",
        clientDni: client?.dni || client?.taxId || "",
        clientTaxId: client?.taxId || "",
        clientPhone: client?.phone || "",
        clientEmail: proposal.clientEmail,
        address: proposal.address,
        description: proposal.description,
        status: CONTRACT_STATUS.PENDING,
        version: "1.0",
        versions: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: "",
        isActive: true,
      };

      await addDoc(contractData);

      navigate("/proposals");
    } catch (error) {
      console.error("Error accepting proposal:", error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!proposal) return <EmptyState title="Propuesta no encontrada" />;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#e2e8f0",
        background: theme.palette.background.paper,
      }}
    >
      {/* CABECERA */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 1, sm: 2 },
          mb: { xs: 3, sm: 4 },
          flexWrap: "wrap",
        }}
      >
        <IconButton
          onClick={() => navigate("/proposals")}
          sx={{
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.12)"
                : "#e2e8f0",
            mt: 0.5,
          }}
        >
          <ArrowBack sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              wordBreak: "break-word",
            }}
          >
            {proposal.title}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
            <Chip
              label={PROPOSAL_STATUS_LABELS[proposal.status]}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
              }}
              color={
                proposal.status === PROPOSAL_STATUS.ACCEPTED
                  ? "success"
                  : proposal.status === PROPOSAL_STATUS.REJECTED
                    ? "error"
                    : proposal.status === PROPOSAL_STATUS.SENT
                      ? "info"
                      : proposal.status === PROPOSAL_STATUS.MODIFICATION
                        ? "secondary"
                        : "warning"
              }
              variant="outlined"
            />
            <Chip
              icon={<Business sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              label={proposal.clientName}
              size="small"
              variant="outlined"
              sx={{
                borderColor: theme.palette.secondary.main,
                color: theme.palette.secondary.main,
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
              }}
            />
            {proposal.version && (
              <Chip
                label={`v${proposal.version}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              />
            )}
            {proposal.entryChannel &&
              ENTRY_CHANNEL_LABELS?.[proposal.entryChannel] && (
                <Chip
                  label={`Canal: ${ENTRY_CHANNEL_LABELS[proposal.entryChannel]}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: theme.palette.info.main,
                    color: theme.palette.info.main,
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  }}
                />
              )}
            {/* Mostrar chip con el nombre del contacto del canal */}
            {showChannelContact && (
              <Chip
                icon={
                  proposal.entryChannel === ENTRY_CHANNELS.COMMERCIAL ? (
                    <Handshake sx={{ fontSize: 14 }} />
                  ) : (
                    <Engineering sx={{ fontSize: 14 }} />
                  )
                }
                label={`${proposal.entryChannel === ENTRY_CHANNELS.COMMERCIAL ? "Comercial: " : "EPC: "}${channelContact.firstName} ${channelContact.lastName}`}
                size="small"
                color="info"
                variant="outlined"
                sx={{
                  borderColor: theme.palette.info.main,
                  color: theme.palette.info.main,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                }}
              />
            )}
            {proposal.commercialPercentage && (
              <Chip
                label={`${proposal.commercialPercentage}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              />
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1}>
          {proposal.status !== PROPOSAL_STATUS.ACCEPTED && (
            <Button
              variant="contained"
              color="success"
              onClick={handleAccept}
              disabled={updating}
              sx={{ borderRadius: "12px", height: { xs: 36, sm: 44 }, boxShadow: "none", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
            >
              {updating ? "Aceptando..." : "Aceptar propuesta"}
            </Button>
          )}
        </Stack>
      </Box>

      {/* CONTENIDO PRINCIPAL */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 2, sm: 3 },
          flexWrap: "wrap",
          width: "100%",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* INFORMACIÓN - LADO IZQUIERDO */}
        <Box
          sx={{
            flex: { md: "1 1 340px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "300px" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 2, sm: 3 },
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "#e2e8f0",
              background: theme.palette.background.paper,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 3,
                  color: theme.palette.primary.main,
                  borderBottom: "2px solid",
                  borderColor: theme.palette.primary.main,
                  pb: 1,
                  display: "block",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Información
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
              >
                Cliente
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  mt: 0.5,
                }}
              >
                <Person
                  sx={{ fontSize: { xs: 16, sm: 18 }, color: theme.palette.primary.main }}
                />
                <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                  {proposal.clientName || "—"}
                </Typography>
              </Box>

              {proposal.clientEmail && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    Email
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                      mt: 0.5,
                    }}
                  >
                    <Email
                      sx={{ fontSize: { xs: 16, sm: 18 }, color: theme.palette.primary.main }}
                    />
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" }, wordBreak: "break-all" }}>
                      {proposal.clientEmail}
                    </Typography>
                  </Box>
                </>
              )}

              {proposal.address && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    Dirección
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      mb: 2,
                      mt: 0.5,
                    }}
                  >
                    <LocationOn
                      sx={{
                        fontSize: { xs: 16, sm: 18 },
                        color: theme.palette.error.main,
                        mt: 0.3,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                      {formatAddress(proposal.address)}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Información del contacto del canal (Comercial o EPC) */}
              {showChannelContact && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      mb: 1,
                      color: theme.palette.info.main,
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    }}
                  >
                    {proposal.entryChannel === ENTRY_CHANNELS.COMMERCIAL
                      ? "Comercial asignado"
                      : "EPC asignado"}
                  </Typography>

                  {channelContact.email && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Email
                        sx={{ fontSize: { xs: 16, sm: 18 }, color: theme.palette.info.main }}
                      />
                      <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" }, wordBreak: "break-all" }}>
                        {channelContact.email}
                      </Typography>
                    </Box>
                  )}

                  {channelContact.phone && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ minWidth: 20 }}
                      >
                        📞
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                        {channelContact.phone}
                      </Typography>
                    </Box>
                  )}

                  {proposal.commercialPercentage && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                        mt: 1,
                      }}
                    >
                      <Chip
                        label={`${proposal.commercialPercentage}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                      />
                    </Box>
                  )}
                </>
              )}

              {/* ARCHIVOS */}
              {proposal.files && proposal.files.length > 0 && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ 
                      mb: 2,
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    }}
                  >
                    <AttachFile sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    Archivos adjuntos
                  </Typography>
                  <List dense disablePadding>
                    {proposal.files.map((file, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Description sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.info.main }} />
                        </ListItemIcon>
                        <ListItemText>
                          <Link
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              fontSize: { xs: "0.8rem", sm: "0.85rem" },
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              wordBreak: 'break-all',
                              '&:hover': {
                                color: theme.palette.primary.dark,
                              },
                            }}
                          >
                            {file.name || file.url}
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </Link>
                        </ListItemText>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {/* Versión */}
              {proposal.version && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    Versión
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="primary"
                    sx={{ mt: 0.5, fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    v{proposal.version}
                  </Typography>
                  {proposal.versions?.length > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5, fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                    >
                      {proposal.versions.length} cambio(s) registrado(s)
                    </Typography>
                  )}
                </>
              )}

              {proposal.notes && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    Notas
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                    {proposal.notes}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>

          {/* FACTURA */}
          {proposal.hasInvoice && (
            <Card
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: "1px solid",
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.12)"
                    : "#e2e8f0",
                background: theme.palette.background.paper,
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{
                    mb: 2,
                    color: theme.palette.primary.main,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  }}
                >
                  Factura de luz
                </Typography>

                {proposal.invoiceLink ? (
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      component="a"
                      href={proposal.invoiceLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      startIcon={<OpenInNew />}
                      sx={{ borderRadius: "10px", textTransform: "none", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                    >
                      Ver factura
                    </Button>
                  </Box>
                ) : (
                  <Chip
                    label="Pendiente de enlace"
                    size="small"
                    color="default"
                    variant="outlined"
                    sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                  />
                )}
              </CardContent>
            </Card>
          )}
        </Box>

        {/* DESCRIPCIÓN - LADO DERECHO */}
        <Box
          sx={{
            flex: { md: "2 1 540px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "350px" },
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "#e2e8f0",
              background: theme.palette.background.paper,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  borderBottom: "2px solid",
                  borderColor: theme.palette.primary.main,
                  pb: 1,
                  display: "inline-block",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Descripción
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "pre-wrap", fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
              >
                {proposal.description || "Sin descripción"}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Paper>
  );
};

export default ProposalDetail;